import {
  MoreHorizontal,
  PackageX,
  AlertCircle,
  ArrowDownUp,
  TrendingUp,
  Layers,
  Store,
  Tag,
  Activity,
} from 'lucide-react';
import { round } from '@/utils/mathUtils';
import { JSX, memo, useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { IItemDataCount } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';

// Status mappings for consistent rendering
type StockStatus = 'needStock' | 'canTransfer' | 'overStock' | 'noTrans';

interface StatusConfig {
  label: string;
  longLabel: string;
  icon: JSX.Element;
  className: string;
}

// Status configuration map - centralized styling and labels
const STATUS_CONFIG: Record<StockStatus, StatusConfig> = {
  needStock: {
    label: 'يحتاج',
    longLabel: 'يحتاج مخزون',
    icon: <AlertCircle className="h-3 w-3" />,
    className: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300',
  },
  canTransfer: {
    label: 'تحويل',
    longLabel: 'يمكن تحويل',
    icon: <ArrowDownUp className="h-3 w-3" />,
    className:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300',
  },
  overStock: {
    label: 'زيادة',
    longLabel: 'رصيد زيادة',
    icon: <TrendingUp className="h-3 w-3" />,
    className:
      'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300',
  },
  noTrans: {
    label: 'لا حركة',
    longLabel: 'لا يوجد حركة',
    icon: <Layers className="h-3 w-3" />,
    className:
      'bg-gray-100 text-gray-700 dark:bg-gray-950/30 dark:text-gray-300',
  },
};

// Helper function to format numbers consistently
const formatNumber = (num: number) => {
  return round(num, 2).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

// Memoized Status Badge component
const StatusBadge = memo(
  ({
    status,
    difference,
    isCompact = false,
  }: {
    status: StockStatus;
    difference: number;
    isCompact?: boolean;
  }) => {
    const config = STATUS_CONFIG[status];
    return (
      <Badge
        variant="outline"
        className={`flex items-center gap-1 rounded-sm px-1 text-xs ${config.className}`}
      >
        {config.icon}
        {isCompact ? config.label : config.longLabel}{' '}
        {difference > 0 && `(${difference})`}
      </Badge>
    );
  },
);
StatusBadge.displayName = 'StatusBadge';

// Store label component to improve readability
const StoreLabel = memo(
  ({
    type,
    count,
    tooltipLabel,
  }: {
    type: 'retail' | 'wholesale';
    count: number;
    tooltipLabel: string;
  }) => {
    const bgColor =
      type === 'retail'
        ? 'bg-indigo-100 dark:bg-indigo-900/30'
        : 'bg-purple-100 dark:bg-purple-900/30';

    const textColor =
      type === 'retail'
        ? 'text-indigo-700 dark:text-indigo-300'
        : 'text-purple-700 dark:text-purple-300';

    const icon =
      type === 'retail' ? (
        <Tag className="h-3 w-3" />
      ) : (
        <Store className="h-3 w-3" />
      );

    const label = type === 'retail' ? 'قطاعي' : 'جملة';

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`flex items-center gap-1 rounded-full ${bgColor} px-2 py-0.5`}
            >
              {icon}
              <span className={`text-xs font-medium ${textColor}`}>
                {label}
              </span>
              <Badge
                variant="outline"
                className={`ml-1 rounded-full px-1.5 py-0 text-[10px] font-semibold ${textColor}`}
              >
                {count}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">{tooltipLabel}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
StoreLabel.displayName = 'StoreLabel';

// Price badge component
const PriceBadge = memo(({ price }: { price: number | null }) => (
  <div className="flex items-center overflow-hidden rounded-md border border-primary/20 shadow-sm">
    <span className="flex h-6 items-center justify-center border-r border-primary/20 bg-primary/10 px-1.5 text-xs font-medium text-primary">
      السعر
    </span>
    <div className="flex h-6 items-center justify-center bg-primary/5 px-2 text-sm font-medium text-primary">
      {price ? formatNumber(price) : '-'}
    </div>
  </div>
));
PriceBadge.displayName = 'PriceBadge';

// Memoized inventory status component
const InventoryStatus = memo(
  ({
    count,
    netSales,
    total = 100,
  }: {
    count: number;
    netSales: number;
    total?: number;
  }) => {
    const ratio = Math.min(100, Math.max(0, (count / total) * 100));
    let color = 'bg-green-500';

    if (ratio < 20) {
      color = 'bg-red-500';
    } else if (ratio < 50) {
      color = 'bg-amber-500';
    }

    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">المخزون</span>
          <div className="flex items-center gap-1.5">
            <span className="font-medium">
              {count}/{netSales + count}
            </span>
            <span className="text-[10px] text-muted-foreground">
              ({Math.round(ratio)}%)
            </span>
          </div>
        </div>
        <Progress value={ratio} className={`h-1.5 ${color}`} />
      </div>
    );
  },
);
InventoryStatus.displayName = 'InventoryStatus';

// Memoized Store Stats component for dropdowns
const StoreStats = memo(
  ({
    title,
    status,
    count,
    sales,
    returns,
    netSales,
  }: {
    title: string;
    status: StockStatus;
    count: number;
    sales: number;
    returns: number;
    netSales: number;
  }) => {
    const difference = Math.abs(count - netSales);
    const total = count + netSales;

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold">{title}</div>
          <StatusBadge status={status} difference={difference} />
        </div>

        <InventoryStatus count={count} netSales={netSales} total={total} />

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">رصيد:</span>
              <span className="font-medium">{count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">بيع:</span>
              <span className="font-medium">{sales}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">مرتجع:</span>
              <span className="font-medium">{returns}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">صافي بيع:</span>
              <span className="font-medium">{netSales}</span>
            </div>
            <div className="flex justify-between border-t border-dashed border-gray-200 pt-1 dark:border-gray-700">
              <span className="text-muted-foreground">الفرق:</span>
              <span className="font-medium text-primary">{difference}</span>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
StoreStats.displayName = 'StoreStats';

// Memoized Item Card component
const ItemCard = memo(({ item }: { item: IItemDataCount }) => {
  const retailDifference = Math.abs(item.Store1Count - item.Store1NetSales);
  const wholeSaleDifference = Math.abs(item.Store2Count - item.Store2NetSales);
  const hasNeedStock =
    item.RetailStatus === 'needStock' || item.WholeSaleStatus === 'needStock';

  return (
    <div
      className={`relative overflow-hidden rounded-lg border bg-card p-3 text-sm shadow-sm transition-all hover:shadow ${
        hasNeedStock ? 'border-red-500/50' : 'hover:border-primary/50'
      }`}
    >
      {hasNeedStock && (
        <div className="absolute -right-1 -top-1 h-2 w-2 animate-ping rounded-full bg-red-500" />
      )}

      {/* Item Header: Name & Price */}
      <div className="mb-3 flex flex-col gap-1.5 border-b border-border/50 pb-2 sm:flex-row sm:items-center sm:justify-between">
        <h3
          className="line-clamp-1 text-sm font-medium text-foreground"
          title={item.Name || '-'}
        >
          {item.Name || 'صنف غير معروف'}
        </h3>
        <PriceBadge price={item.Price} />
      </div>

      {/* Status Indicators */}
      <div className="space-y-4">
        {/* Retail Section (قطاعي) */}
        <div className="rounded-md border border-indigo-100 bg-indigo-50/50 p-2 dark:border-indigo-900/30 dark:bg-indigo-900/10">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
              <span className="font-medium text-indigo-700 dark:text-indigo-300">
                بيانات القطاعي
              </span>
            </div>
            <StatusBadge
              status={item.RetailStatus}
              difference={retailDifference}
              isCompact
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-sm bg-white p-1.5 dark:bg-gray-800">
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                رصيد القطاعي
              </span>
              <span className="text-sm font-medium">{item.Store1Count}</span>
            </div>

            <div className="rounded-sm bg-white p-1.5 dark:bg-gray-800">
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                مبيعات القطاعي
              </span>
              <span className="text-sm font-medium">{item.Store1Sales}</span>
            </div>
          </div>

          <div className="mt-2 space-y-0.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-indigo-700 dark:text-indigo-300">
                حالة المخزون:
              </span>
              <div className="flex items-center gap-1.5">
                <span
                  className={`rounded-full px-1.5 text-xs ${
                    item.RetailStatus === 'needStock'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      : item.RetailStatus === 'canTransfer'
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : item.RetailStatus === 'overStock'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {item.RetailStatus === 'needStock'
                    ? 'يحتاج طلب'
                    : item.RetailStatus === 'canTransfer'
                      ? 'يمكن تحويل'
                      : item.RetailStatus === 'overStock'
                        ? 'زيادة مخزون'
                        : 'لا حركة'}
                </span>
              </div>
            </div>

            <Progress
              value={
                (item.Store1Count /
                  Math.max(1, item.Store1Count + item.Store1NetSales)) *
                100
              }
              className={`h-1.5 ${
                item.RetailStatus === 'needStock'
                  ? 'bg-red-500/50'
                  : item.RetailStatus === 'canTransfer'
                    ? 'bg-yellow-500/50'
                    : item.RetailStatus === 'overStock'
                      ? 'bg-green-500/50'
                      : 'bg-gray-400/50'
              }`}
            />
          </div>
        </div>

        {/* Wholesale Section (جملة) */}
        <div className="rounded-md border border-purple-100 bg-purple-50/50 p-2 dark:border-purple-900/30 dark:bg-purple-900/10">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Store className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
              <span className="font-medium text-purple-700 dark:text-purple-300">
                بيانات الجملة
              </span>
            </div>
            <StatusBadge
              status={item.WholeSaleStatus}
              difference={wholeSaleDifference}
              isCompact
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-sm bg-white p-1.5 dark:bg-gray-800">
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                رصيد الجملة
              </span>
              <span className="text-sm font-medium">{item.Store2Count}</span>
            </div>

            <div className="rounded-sm bg-white p-1.5 dark:bg-gray-800">
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                مبيعات الجملة
              </span>
              <span className="text-sm font-medium">{item.Store2Sales}</span>
            </div>
          </div>

          <div className="mt-2 space-y-0.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-purple-700 dark:text-purple-300">
                حالة المخزون:
              </span>
              <div className="flex items-center gap-1.5">
                <span
                  className={`rounded-full px-1.5 text-xs ${
                    item.WholeSaleStatus === 'needStock'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      : item.WholeSaleStatus === 'canTransfer'
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                        : item.WholeSaleStatus === 'overStock'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {item.WholeSaleStatus === 'needStock'
                    ? 'يحتاج طلب'
                    : item.WholeSaleStatus === 'canTransfer'
                      ? 'يمكن تحويل'
                      : item.WholeSaleStatus === 'overStock'
                        ? 'زيادة مخزون'
                        : 'لا حركة'}
                </span>
              </div>
            </div>

            <Progress
              value={
                (item.Store2Count /
                  Math.max(1, item.Store2Count + item.Store2NetSales)) *
                100
              }
              className={`h-1.5 ${
                item.WholeSaleStatus === 'needStock'
                  ? 'bg-red-500/50'
                  : item.WholeSaleStatus === 'canTransfer'
                    ? 'bg-yellow-500/50'
                    : item.WholeSaleStatus === 'overStock'
                      ? 'bg-green-500/50'
                      : 'bg-gray-400/50'
              }`}
            />
          </div>
        </div>
      </div>

      {/* View details button */}
      <div className="mt-3 flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="group flex h-7 items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700/70">
              <Activity className="h-3 w-3" />
              تفاصيل إضافية
              <MoreHorizontal className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-0">
            <div className="space-y-4 p-4">
              <StoreStats
                title="تفاصيل القطاعي"
                status={item.RetailStatus}
                count={item.Store1Count}
                sales={item.Store1Sales}
                returns={item.Store1Returns}
                netSales={item.Store1NetSales}
              />

              <Separator className="my-2" />

              <StoreStats
                title="تفاصيل الجملة"
                status={item.WholeSaleStatus}
                count={item.Store2Count}
                sales={item.Store2Sales}
                returns={item.Store2Returns}
                netSales={item.Store2NetSales}
              />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
});
ItemCard.displayName = 'ItemCard';

// Empty state components
const EmptyItems = memo(() => (
  <Card className="mt-4 border-dashed">
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <PackageX className="h-5 w-5 text-gray-400" />
        <span className="text-sm">لا توجد أصناف متاحة 📦 ❌</span>
      </div>
    </CardContent>
  </Card>
));
EmptyItems.displayName = 'EmptyItems';

const NoMovementItems = memo(() => (
  <Card className="mt-2 border-dashed border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
    <CardContent className="p-3">
      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm">لا توجد أصناف عليها حركة 📊 ⚠️</span>
      </div>
    </CardContent>
  </Card>
));
NoMovementItems.displayName = 'NoMovementItems';

// Main component with optimized rendering
export const ItemsPriceCard = memo(({ items }: { items: IItemDataCount[] }) => {
  // Filter items with memoization to prevent recalculation
  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) return [];

    return items.filter(
      (item) =>
        item.RetailStatus !== 'noTrans' || item.WholeSaleStatus !== 'noTrans',
    );
  }, [items]);

  // Handle empty states
  if (!items || items.length === 0) {
    return <EmptyItems />;
  }

  if (filteredItems.length === 0) {
    return <NoMovementItems />;
  }

  // Render the grid of items - now with full width
  return (
    <div className="grid grid-cols-1 gap-3">
      {filteredItems.map((item, index) => (
        <ItemCard key={`item-${index}-${item.ID || index}`} item={item} />
      ))}
    </div>
  );
});

ItemsPriceCard.displayName = 'ItemsPriceCard';
